import { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import $ from 'jquery'
import { withTranslation } from 'react-i18next'

import i18n, { SUPPORTED_LANGUAGES } from '@/i18n'
import { canSaveUserLocale, updateUserLocale } from '@/api/account'
import {
  selectLocaleBuilding,
  writePreferredLanguage,
} from '@/components/providers/user-locale-sync'
import { getLanguageOptions } from '@/i18n/locale-resolution'
import { toastFactory } from '@/factories'
import { isImpersonating } from '@/utils/auth'
import { getApiErrorMessage } from '@/utils/error'

import ImageField from '../FormLegacy/FormFields/ImageField'
import TextField from '../FormLegacy/FormFields/TextField'
import EmailField from '../FormLegacy/FormFields/EmailField'
import BuildingCodeField from '../FormLegacy/FormFields/BuildingCodeField'
import BirthDateField from '../FormLegacy/FormFields/BirthDateField'
import GenderField from '../FormLegacy/FormFields/GenderField'
import PhoneField from '../FormLegacy/FormFields/PhoneField'
import CheckboxField from '../FormLegacy/FormFields/CheckboxField'
import DefaultBuildingField from '../FormLegacy/FormFields/DefaultBuildingField'
import ConfirmPopup from '../Popups/ConfirmPopup'
import { getAssetLink } from '../../helpers/get-asset-link'
import API from '../../api/api'
import { setUserData } from '../../reducers/userDataReducer'
import {
  selectActiveBuilding,
  selectBuildingsForFilter,
  setActiveBuilding,
} from '../../reducers/buildingSelectorReducer'
import Tooltip from '../CreateEvent/components/Tooltip/Tooltip'

// Language picker (EL-13870): shown to every role that reaches this page, but only when the user's
// building has translations on (`i18nAvailable`), and it lists that building's `availableLocale`
// (getLanguageOptions in src/i18n/locale-resolution.ts). Calling i18n.changeLanguage() directly
// (not a redux-form field) is intentional: the language applies and saves on pick, not with the
// profile form's Save. It is saved to the account (`user.locale`), which UserLocaleSync
// (src/components/providers/user-locale-sync) applies on every boot/login on any browser - except
// for Administrator, whose PUT /admin can't store it yet: their pick is only remembered in this
// browser (preferred-language.ts), which the same rules fall back to.

const LANGUAGE_LABELS = {
  'en-US': 'English (US)',
  'es-US': 'Español (EE. UU.)',
  'fr-CA': 'Français (Canada)',
  // Same build-time condition as INTERNAL_LOCALES_ENABLED in src/i18n/index.ts (plain `===` so Vite
  // folds it and the pl/uk labels are dropped from pre-prod/production bundles too).
  ...(import.meta.env.MODE === 'development' || import.meta.env.MODE === 'staging'
    ? { pl: 'Polski', uk: 'Українська' }
    : {}),
}

// Note: i18n is configured with `load: 'currentOnly'` (src/i18n/index.ts), so
// i18n.language always resolves to an exact SUPPORTED_LANGUAGES entry (or
// fallbackLng) - i18next's own language negotiation handles matching
// unsupported/region variants (e.g. a stale pre-rename "en" or a future
// "en-GB") before this ever runs. This is just a defensive fallback in case
// SUPPORTED_LANGUAGES ever gets out of sync with what's actually cached.
const normalizeLanguage = (language) =>
  SUPPORTED_LANGUAGES.includes(language) ? language : SUPPORTED_LANGUAGES[0]

class UserProfile extends Component {
  constructor(props) {
    super(props)
    this.state = {
      firstNameError: false,
      lastNameError: false,
      birthError: false,
      phoneError: false,
      codeError: false,
      streetError: false,
      apartmentError: false,
      zipCodeError: false,
      formPending: false,
      formError: '',
      formSuccess: false,
      isDeletePopupOpen: false,
      language: normalizeLanguage(i18n.language),
      isLanguageDropdownOpen: false,
      isLanguageSaving: false,
    }
    this.isFieldValid = this.isFieldValid.bind(this)
    this.setFieldValidState = this.setFieldValidState.bind(this)
    this.validateField = this.validateField.bind(this)
    this.updateDataFor = this.updateDataFor.bind(this)
    this.updatePropertyManagerData = this.updatePropertyManagerData.bind(this)
    this.updateTrainerData = this.updateTrainerData.bind(this)
    this.updateClientData = this.updateClientData.bind(this)
    this.submitForm = this.submitForm.bind(this)
    this.deleteAccount = this.deleteAccount.bind(this)
    this.renderDeleteClient = this.renderDeleteClient.bind(this)
    this.submitSuccessMessage = this.submitSuccessMessage.bind(this)
    this.handleI18nLanguageChanged = this.handleI18nLanguageChanged.bind(this)
    this.languageClickOutsideHandler = this.languageClickOutsideHandler.bind(this)
    this.toggleLanguageDropdown = this.toggleLanguageDropdown.bind(this)
    this.selectLanguage = this.selectLanguage.bind(this)
  }

  componentDidMount() {
    i18n.on('languageChanged', this.handleI18nLanguageChanged)
    $(document.body).on('click', this.languageClickOutsideHandler)
  }

  componentWillUnmount() {
    i18n.off('languageChanged', this.handleI18nLanguageChanged)
    $(document.body).off('click', this.languageClickOutsideHandler)
  }

  handleI18nLanguageChanged(language) {
    this.setState({ language: normalizeLanguage(language) })
  }

  languageClickOutsideHandler(e) {
    if ($(e.target).closest('.field-select-language .control').length === 0) {
      this.setState({ isLanguageDropdownOpen: false })
    }
  }

  toggleLanguageDropdown(e) {
    e.preventDefault()
    this.setState((state) => ({ isLanguageDropdownOpen: !state.isLanguageDropdownOpen }))
  }

  async selectLanguage(e, language) {
    e.preventDefault()
    this.setState({ isLanguageDropdownOpen: false })
    // One save at a time: an overlapping pick's failure would roll back to a stale language.
    if (this.state.isLanguageSaving) return

    const { dispatch, userData } = this.props
    const previousLanguage = this.state.language
    i18n.changeLanguage(language)

    // While impersonating nothing is remembered or saved: the support user's pick must not land on
    // the impersonated account.
    if (isImpersonating()) return
    writePreferredLanguage(userData.user.id, language)

    // Not saved when it is already the saved choice, or for a role that can't save it
    // (Administrator - the browser-remembered language above is all it gets). Compared against
    // user.locale, not the displayed language: a user whose user.locale is still null must be able
    // to save the language they already see.
    if (language === userData.user.locale || !canSaveUserLocale(userData.user.role)) return

    this.setState({ isLanguageSaving: true })
    try {
      await updateUserLocale({ role: userData.user.role, id: userData.id, locale: language })
      // Only `locale` is merged: the per-role PUT responses aren't the GET payload userData holds.
      dispatch(setUserData({ user: { ...this.props.userData.user, locale: language } }))
    } catch (error) {
      // The account still holds the old language and would switch back on the next boot - roll
      // back now instead of showing a choice that didn't stick.
      i18n.changeLanguage(previousLanguage)
      writePreferredLanguage(userData.user.id, previousLanguage)
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      this.setState({ isLanguageSaving: false })
    }
  }

  isFieldValid(fieldName) {
    return this.props.form[fieldName].isValid
  }

  setFieldValidState(fieldName) {
    const { form } = this.props
    if (form[fieldName].isValid) {
      this.setState({ [`${fieldName}Error`]: false })
    } else {
      this.setState({ [`${fieldName}Error`]: true })
    }
  }

  validateField(fieldName) {
    this.setFieldValidState(fieldName)
    return this.isFieldValid(fieldName)
  }

  updateDataFor(role) {
    const isFirstName = this.validateField('firstName')
    const isLastName = this.validateField('lastName')
    const { userData } = this.props
    const isAdmin = userData.user.role === 'Administrator'

    if (isAdmin) {
      const { defaultBuildingId } = this.props.form
      const newUserData = {
        defaultBuildingId: defaultBuildingId ? defaultBuildingId.value : null,
      }
      this.setState({ formPending: true })
      API.updateUserData(newUserData, '', 'admin').then((res) => {
        const { dispatch, buildings } = this.props
        this.setState({ formPending: false, codeError: false, formError: '' })
        dispatch(setUserData(res))

        if (defaultBuildingId?.value) {
          const newActiveBuilding = buildings.find(
            (building) => String(building.id) === String(defaultBuildingId.value),
          )
          if (newActiveBuilding) {
            dispatch(setActiveBuilding(newActiveBuilding))
          }
        }

        this.submitSuccessMessage()
      })
      return
    }
    if (isFirstName && isLastName) {
      const { firstName, lastName, avatar, phone, defaultBuildingId } = this.props.form
      const newUserData = {
        firstName: firstName.value,
        lastName: lastName.value,
        defaultBuildingId: defaultBuildingId ? defaultBuildingId.value : null,
      }
      if (avatar.file !== false) {
        newUserData.avatar = avatar.file
      }
      if (['PropertyManager', 'Staff', 'ShiftManager'].includes(userData.user.role)) {
        if (phone.value && phone.value.length <= 15) {
          newUserData.phone = phone.value
        }
        if (!phone.value) {
          newUserData.phone = null
        }
        if (phone.value && phone.value.length > 15) {
          this.setState({ formError: this.props.t('user-panel:profile.phoneTooLong') })
          return
        }
      }
      this.setState({ formPending: true })
      API.updateUserData(newUserData, userData.id, role).then((res) => {
        const { dispatch, buildings } = this.props
        this.setState({ formPending: false, codeError: false, formError: '' })
        dispatch(setUserData(res))

        if (defaultBuildingId?.value) {
          const newActiveBuilding = buildings.find(
            (b) => String(b.id) === String(defaultBuildingId.value),
          )
          if (newActiveBuilding) {
            dispatch(setActiveBuilding(newActiveBuilding))
          }
        }

        this.submitSuccessMessage()
      })
    }
  }

  updatePropertyManagerData() {
    const isFirstName = this.validateField('firstName')
    const isLastName = this.validateField('lastName')
    if (isFirstName && isLastName) {
      const { userData } = this.props
      const { firstName, lastName, avatar, gender, defaultBuildingId } = this.props.form
      const newUserData = {
        firstName: firstName.value,
        lastName: lastName.value,
        gender: gender.value,
        defaultBuildingId: defaultBuildingId ? defaultBuildingId.value : null,
      }
      if (avatar.file !== false) {
        newUserData.avatar = avatar.file
      }
      this.setState({ formPending: true })
      API.updateManagementUserData(newUserData, userData.id, 'propertyManager').then((res) => {
        const { dispatch, buildings } = this.props
        this.setState({ formPending: false, codeError: false, formError: '' })
        dispatch(setUserData(res))

        if (defaultBuildingId?.value) {
          const newActiveBuilding = buildings.find(
            (b) => String(b.id) === String(defaultBuildingId.value),
          )
          if (newActiveBuilding) {
            dispatch(setActiveBuilding(newActiveBuilding))
          }
        }

        this.submitSuccessMessage()
      })
    }
  }

  updateTrainerData() {
    const isFirstName = this.validateField('firstName')
    const isLastName = this.validateField('lastName')
    const isPhone = this.validateField('phone')
    if (isFirstName && isLastName && isPhone) {
      const { userData } = this.props
      const {
        firstName,
        lastName,
        gender,
        phone,
        avatar,
        street,
        apartment,
        zipCode,
        defaultBuildingId,
      } = this.props.form
      const newUserData = {
        firstName: firstName.value,
        lastName: lastName.value,
        gender: gender.value,
        phone: phone.value,
        street: street.value,
        apartment: apartment.value,
        zipCode: zipCode.value,
        defaultBuildingId: defaultBuildingId ? defaultBuildingId.value : null,
      }
      if (avatar.file !== false) {
        newUserData.avatar = avatar.file
      }
      this.setState({ formPending: true })
      API.updateUserData(newUserData, userData.id, 'trainer')
        .then((res) => {
          const { dispatch } = this.props
          this.setState({ formPending: false, codeError: false, formError: '' })
          dispatch(setUserData(res))
          this.submitSuccessMessage()
        })
        .catch(() => {
          this.setState({
            formPending: false,
            formError: this.props.t('user-panel:profile.serverError'),
          })
        })
    }
  }

  updateClientData() {
    const isFirstName = this.validateField('firstName')
    const isLastName = this.validateField('lastName')
    const isBirthDate = this.validateField('birth')
    const isPhone = this.validateField('phone')
    const isStreet = this.validateField('street')
    const isApartment = this.validateField('apartment')
    const isZipCode = this.validateField('zipCode')
    if (
      isFirstName &&
      isLastName &&
      isBirthDate &&
      isPhone &&
      isStreet &&
      isApartment &&
      isZipCode
    ) {
      const { userData } = this.props
      const {
        firstName,
        lastName,
        birth,
        gender,
        phone,
        code,
        notification,
        newsletter,
        avatar,
        street,
        apartment,
        zipCode,
        defaultBuildingId,
      } = this.props.form
      const newUserData = {
        firstName: firstName.value,
        lastName: lastName.value,
        birth: birth.value,
        gender: gender.value,
        phone: phone.value,
        code: code.value,
        notification: notification.value,
        newsletter: newsletter.value,
        street: street.value,
        apartment: apartment.value,
        zipCode: zipCode.value,
        defaultBuildingId: defaultBuildingId ? defaultBuildingId.value : null,
      }
      if (avatar.file !== false) {
        newUserData.avatar = avatar.file
      }
      this.setState({ formPending: true })
      API.updateUserData(newUserData, userData.id)
        .then((res) => {
          const { dispatch } = this.props
          this.setState({ formPending: false, codeError: false, formError: '' })
          dispatch(setUserData(res))
          this.submitSuccessMessage()
        })
        .catch((err) => {
          if (err === 404) {
            this.setState({
              formPending: false,
              codeError: true,
              formError: this.props.t('user-panel:profile.incorrectBuildingCode'),
            })
          } else if (err === 403) {
            this.setState({
              formPending: false,
              formError: this.props.t('user-panel:profile.contactPropertyManagement'),
            })
          } else {
            this.setState({
              formPending: false,
              formError: this.props.t('user-panel:profile.serverError'),
            })
          }
        })
    }
  }

  submitForm(e) {
    e.preventDefault()
    const { userData } = this.props
    switch (userData.user.role) {
      case 'Administrator':
        this.updateDataFor('admin')
        break
      case 'Manager':
        this.updateDataFor('manager')
        break
      case 'Staff':
        this.updateDataFor('staff')
        break
      case 'PropertyManager':
        this.updatePropertyManagerData()
        break
      case 'Trainer':
        this.updateTrainerData()
        break
      default:
        this.updateClientData()
    }
  }

  deleteAccount() {
    API.deleteClient(this.props.userData.user.id)
      .then(() => {
        this.setState({ formPending: false, codeError: false, formError: '' })
        this.submitSuccessMessage()
      })
      .catch((err) => {
        if (err === 404) {
          this.setState({
            formPending: false,
            codeError: true,
            formError: this.props.t('user-panel:profile.noClientFound'),
          })
        } else {
          this.setState({
            formPending: false,
            formError: this.props.t('user-panel:profile.serverError'),
          })
        }
      })
  }

  renderDeleteClient() {
    const { entrataId, yardiId, realPageId } = this.props.userData
    const { t } = this.props
    return entrataId || yardiId || realPageId ? (
      <p className="form--object-profile__contact-info">
        {t('user-panel:profile.deleteContactAdmin')}
      </p>
    ) : (
      <button
        className={`button is-block button-red button-margin-top ${
          this.state.formPending ? 'is-loading' : ''
        }`}
        onClick={() => this.setState({ isDeletePopupOpen: true })}
      >
        {t('user-panel:profile.deleteAccount')}
      </button>
    )
  }

  submitSuccessMessage() {
    this.setState({ formSuccess: true })
    setTimeout(() => {
      this.setState({ formSuccess: false })
    }, 2000)
  }

  render() {
    const { userData, _can, buildings, localeBuilding, t } = this.props
    const {
      firstNameError,
      lastNameError,
      birthError,
      phoneError,
      streetError,
      apartmentError,
      zipCodeError,
      formError,
      formPending,
      formSuccess,
    } = this.state
    const isBuildingId = typeof userData.buildingId !== 'undefined' && userData.buildingId !== null
    const code = isBuildingId ? userData.buildingId.code : null
    const birth = new Date(userData.birth)
    const avatarChangeRestricted =
      this.props.avatarChangeRestricted ||
      (isBuildingId && userData.buildingId.avatarChangeRestricted)
    const restrictChangingAvatar = avatarChangeRestricted && userData.user.avatar !== 'avatar.png'
    const nameChangeRestricted =
      this.props.nameChangeRestricted || (isBuildingId && userData.buildingId.nameChangeRestricted)

    const hasMultipleBuildings = buildings?.length > 1
    const hasBuildings = !!buildings?.length
    const buildingOptions = buildings ? buildings.map((b) => ({ value: b.id, label: b.text })) : []

    return (
      <form className="form form--object-profile">
        <div className="image-tooltip-wrap">
          <ImageField
            formType={'userProfile'}
            fieldName={'avatar'}
            imageRatio={1}
            label={t('user-panel:profile.avatar')}
            image={getAssetLink({ path: 'chitown-avatar/avatar', file: userData.user.avatar })}
            defaultImage={getAssetLink({
              path: 'chitown-avatar/avatar',
              file: userData.user.avatar,
              fallback: { file: 'avatar.png' },
            })}
            isDisabled={userData.user.role === 'Administrator' || restrictChangingAvatar}
          />
          {restrictChangingAvatar && (
            <Tooltip
              name="avatarChangeRestricted-tip"
              tooltips={[t('user-panel:profile.avatarRestrictedTooltip')]}
            />
          )}
        </div>
        <div className="columns">
          <div className="column text-tooltip-wrap">
            <TextField
              formType={'userProfile'}
              fieldName={'firstName'}
              label={t('user-panel:profile.firstName')}
              placeholder={t('user-panel:profile.namePlaceholder')}
              defaultValue={userData.user.firstName || ''}
              additionalClass={firstNameError ? 'is-danger' : ''}
              isDisabled={userData.user.role === 'Administrator' || nameChangeRestricted}
            />
            {nameChangeRestricted && (
              <Tooltip
                name="restrictFirstNameEditing-tip"
                tooltips={[t('user-panel:profile.nameRestrictedTooltip')]}
              />
            )}
          </div>
          <div className="column text-tooltip-wrap">
            <TextField
              formType={'userProfile'}
              fieldName={'lastName'}
              label={t('user-panel:profile.lastName')}
              placeholder={t('user-panel:profile.lastName')}
              defaultValue={userData.user.lastName || ''}
              additionalClass={lastNameError ? 'is-danger' : ''}
              isDisabled={userData.user.role === 'Administrator' || nameChangeRestricted}
            />
            {nameChangeRestricted && (
              <Tooltip
                name="restrictLastNameEditing-tip"
                tooltips={[t('user-panel:profile.nameRestrictedTooltip')]}
              />
            )}
          </div>
        </div>
        <EmailField
          formType={'userProfile'}
          fieldName={'email'}
          label={t('user-panel:profile.email')}
          placeholder={t('user-panel:profile.email')}
          defaultValue={userData.user.email || ''}
          showErrorLabel={false}
          additionalClass={''}
          isDisabled={true}
        />
        {['PropertyManager', 'Staff', 'ShiftManager'].includes(userData.user.role) ? (
          <TextField
            formType={'userProfile'}
            fieldName={'phone'}
            label={t('user-panel:profile.phone')}
            placeholder={t('user-panel:profile.phone')}
            defaultValue={userData.phone || ''}
            isDisabled={false}
          />
        ) : null}
        {_can('buildingCode') && (
          <BuildingCodeField
            formType={'userProfile'}
            fieldName={'code'}
            label={t('user-panel:profile.buildingCode')}
            placeholder={t('user-panel:profile.buildingCodePlaceholder')}
            defaultValue={code}
            isDisabled={!_can('editBuildingCode')}
          />
        )}
        {['PropertyManager', 'Staff', 'Administrator'].includes(userData.user.role) &&
          hasBuildings && (
            <DefaultBuildingField
              formType={'userProfile'}
              fieldName={'defaultBuildingId'}
              label={t('user-panel:profile.defaultBuilding')}
              options={buildingOptions}
              defaultValue={userData.defaultBuildingId || buildingOptions[0]?.value || ''}
              placeholder={t('user-panel:profile.defaultBuildingPlaceholder')}
              isDisabled={!hasMultipleBuildings}
            />
          )}
        {localeBuilding?.i18nAvailable === true && (
          <div
            className={`field field-select field-select-language ${
              this.state.isLanguageDropdownOpen ? 'is-open' : ''
            }`}
          >
            <label className="label">{t('user-panel:profile.language')}</label>
            <div className="control">
              <div className="select">
                <div className="field-select__input input" onClick={this.toggleLanguageDropdown}>
                  <span>{LANGUAGE_LABELS[this.state.language] ?? this.state.language}</span>
                </div>
                <div className="field-select__dropdown">
                  {getLanguageOptions({
                    building: localeBuilding,
                    currentLanguage: this.state.language,
                  }).map((language) => (
                    <span
                      key={language}
                      data-value={language}
                      onClick={(e) => this.selectLanguage(e, language)}
                    >
                      {LANGUAGE_LABELS[language] ?? language}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="columns">
          {_can('birthDate') && (
            <div className="column">
              <BirthDateField
                formType={'userProfile'}
                fieldName={'birth'}
                label={t('user-panel:profile.birthDateCode')}
                year={birth.getFullYear()}
                month={birth.getMonth() + 1}
                day={birth.getDate()}
                additionalClass={birthError ? 'is-danger' : ''}
              />
            </div>
          )}
          {_can('gender') && (
            <div className="column">
              <GenderField
                formType={'userProfile'}
                fieldName={'gender'}
                label={t('user-panel:profile.gender')}
                defaultValue={userData.gender}
              />
            </div>
          )}
          {_can('phone') && (
            <div className="column">
              <PhoneField
                formType={'userProfile'}
                fieldName={'phone'}
                label={t('user-panel:profile.mobilePhone')}
                placeholder={t('user-panel:profile.mobilePhone')}
                defaultValue={userData.phone}
                additionalClass={phoneError ? 'is-danger' : ''}
                isDisabled={false}
              />
            </div>
          )}
        </div>
        {_can('address') && (
          <div className="field field-address">
            <div className="columns">
              <div className={'column is-6'}>
                <TextField
                  formType={'userProfile'}
                  fieldName={'street'}
                  label={t('user-panel:profile.address')}
                  placeholder={''}
                  defaultValue={userData.street}
                  additionalClass={streetError ? 'is-danger' : ''}
                  isDisabled={false}
                />
              </div>
              <div className={'column'}>
                <TextField
                  formType={'userProfile'}
                  fieldName={'apartment'}
                  label={t('user-panel:profile.apartment')}
                  placeholder={''}
                  defaultValue={userData.apartment}
                  additionalClass={apartmentError ? 'is-danger' : ''}
                  isDisabled={false}
                />
              </div>
              <div className={'column is-3'}>
                <TextField
                  formType={'userProfile'}
                  fieldName={'zipCode'}
                  label={t('user-panel:profile.zipCode')}
                  placeholder={''}
                  defaultValue={userData.zipCode}
                  additionalClass={zipCodeError ? 'is-danger' : ''}
                  isDisabled={false}
                />
              </div>
            </div>
          </div>
        )}
        {_can('notification') && (
          <CheckboxField
            formType={'userProfile'}
            fieldName={'notification'}
            label={t('user-panel:profile.subscribeReminders')}
            defaultValue={userData.notification}
            isDisabled={false}
          />
        )}
        {_can('newsletter') && (
          <CheckboxField
            formType={'userProfile'}
            fieldName={'newsletter'}
            label={t('user-panel:profile.subscribeNewsletter')}
            defaultValue={userData.newsletter}
            isDisabled={false}
          />
        )}
        {_can('update') && (
          <button
            type="submit"
            className={`button button-primary is-block ${formPending ? 'is-loading' : ''}`}
            onClick={this.submitForm}
          >
            {t('common:actions.save')}
          </button>
        )}
        {userData.user.role === 'Client' && this.renderDeleteClient()}
        <ConfirmPopup
          title={t('user-panel:profile.deleteConfirmTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('common:booleans.yes')}
          isOpen={this.state.isDeletePopupOpen}
          cancelCallback={() => this.setState({ isDeletePopupOpen: false })}
          confirmCallback={this.deleteAccount}
        />
        <p className="has-text-centered help is-danger">{formError}</p>
        {formSuccess && (
          <p className="has-text-centered help is-success">
            {t('user-panel:profile.updateSuccess')}
          </p>
        )}
      </form>
    )
  }
}

UserProfile.propTypes = {
  form: PropTypes.object,
  userData: PropTypes.object,
  dispatch: PropTypes.func,
  _can: PropTypes.func,
  nameChangeRestricted: PropTypes.bool,
  avatarChangeRestricted: PropTypes.bool,
  buildings: PropTypes.array,
  localeBuilding: PropTypes.object,
  t: PropTypes.func,
}

export default connect((state) => {
  const { nameChangeRestricted, avatarChangeRestricted } = selectActiveBuilding(state) || {}

  return {
    userData: state.userData,
    form: state.form.userProfile,
    nameChangeRestricted,
    avatarChangeRestricted,
    buildings: selectBuildingsForFilter(state),
    localeBuilding: selectLocaleBuilding(state),
  }
})(withTranslation(['user-panel', 'common'])(UserProfile))
